import React, { useState, useRef } from 'react';
import './TableList.css';
import type { Table } from '../../types';
import { getTableProductStateClass, resolveTableColor } from '../../utils/tableState';
import { useI18n } from '../../i18n/I18nContext';

// Props for the TableList component.
export interface TableListProps {
  tables: Table[];
  defaultTableColor: string;
  readOnly?: boolean;
  onReorder: (updated: Table[]) => void;
  onTableClick: (table: Table) => void;
}

const TableList: React.FC<TableListProps> = ({ tables, defaultTableColor, readOnly = false, onReorder, onTableClick }) => {
  const { t } = useI18n();
  // Index of the list item currently being dragged over
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Index of the item being dragged
  const draggedIndexRef = useRef<number | null>(null);
  // Whether a drag is in progress
  const draggingRef = useRef<boolean>(false);

  // Handler for drag start; store the dragged index
  function handleDragStart(index: number) {
    if (readOnly) {
      return;
    }
    draggedIndexRef.current = index;
    draggingRef.current = true;
  }
  // Handler for drag over/enter; update placeholder index
  function handleDragOver(e: React.DragEvent<HTMLLIElement>, index: number) {
    if (readOnly) {
      return;
    }
    e.preventDefault();
    setDragOverIndex(index);
  }
  // Handler for drop; compute new order and invoke callback
  function handleDrop(index: number) {
    if (readOnly) {
      draggingRef.current = false;
      setDragOverIndex(null);
      return;
    }
    const from = draggedIndexRef.current;
    if (from === null || from === undefined) {
      draggingRef.current = false;
      setDragOverIndex(null);
      return;
    }
    const updated = [...tables];
    const [moved] = updated.splice(from, 1);
    updated.splice(index, 0, moved);
    onReorder(updated);
    draggedIndexRef.current = null;
    draggingRef.current = false;
    setDragOverIndex(null);
  }
  // Handler for drag end; reset flags
  function handleDragEnd() {
    draggingRef.current = false;
    setDragOverIndex(null);
  }

  return (
    <div className="table-list">
      <h2>{t('table.tables')}</h2>
      <ul>
        {tables.map((t, index) => {
          const productStateClass = getTableProductStateClass(t);
          let itemClass = '';
          if (
            draggingRef.current &&
            (dragOverIndex === index || (dragOverIndex === tables.length && index === tables.length - 1))
          ) {
            itemClass = `${productStateClass} drag-over`.trim();
          } else {
            itemClass = productStateClass;
          }
          return (
            <li
              key={`table-${t.id}`}
              className={itemClass}
              style={{ background: resolveTableColor(t, defaultTableColor) }}
              draggable={!readOnly}
              onDragStart={() => handleDragStart(index)}
              onDragOver={e => handleDragOver(e, index)}
              onDragEnter={e => handleDragOver(e, index)}
              onDrop={() => handleDrop(index)}
              onDragEnd={handleDragEnd}
              onClick={() => onTableClick(t)}
            >
              {t.name}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default TableList;
